import { useCallback, useMemo } from 'react'

import * as _ from 'lodash'
import { DateTime } from 'luxon'

import { useJourneysStore } from '@/features/journeys'
import { calcLevelRewards } from '@/features/journeys/domain/calcLevelRewards'
import type { Challenge, Level } from '@/features/journeys'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import type {
  ProgressRewards,
  JourneyProgress,
  StageProgress,
  LevelProgress
} from '@/shared/types/user-progress'





interface UseLevelProgressProps {
  id: number
}

export const useLevelProgress = ({ id }: UseLevelProgressProps) => {
  const { journeys, getPreviousLevel } = useJourneysStore()

  const { progress, levelWasFinished } = useUserProgress()

  const level = useMemo(() => {
    for (const journey of journeys) {
      for (const stage of journey.active_stages ?? []) {
        const foundLevel = stage.active_levels?.find(l => l.id === id)
        if (foundLevel) return foundLevel
      }
    }
    return undefined
  }, [journeys, id])

  const previousLevel = useMemo(() => {
    if (!level) return null
    return getPreviousLevel(id)
  }, [getPreviousLevel, id, level])

  const levelProgress = useMemo(() => {
    if (!level) return null

    const finished = levelWasFinished(id)

    let blocked = !!level?.locked
    let blockedStatus = 'always'

    if (!blocked && level.locked_until) {
      const lockedUntil = DateTime.fromISO(level.locked_until)
      blocked = lockedUntil.isValid && DateTime.local() < lockedUntil
      if (blocked) blockedStatus = 'time'
    }

    if (!blocked && level.unlock_after_previous_level && previousLevel?.id) {
      blocked = !levelWasFinished(previousLevel.id)
      if (blocked) blockedStatus = 'previousLevel'
    }

    if (!blocked && level.unlock_mission_id) {
      blocked = !progress?.finished_missions.includes(level.unlock_mission_id)
      if (blocked) blockedStatus = 'mission'
    }

    let lastUnfinishedChallenge: Challenge | null = null
    const sortedChallenges = [...(level.active_challenges || [])].sort(
      (a, b) => a.position - b.position
    )

    const correctChallengeIds = new Set(progress?.correct_challenges ?? [])
    const finishedChallengeIds = new Set(progress?.finished_challenges ?? [])

    const {
      finishedChallenges,
      allChallengesFinished,
      ...rewards
    } = calcLevelRewards({
      level,
      challenges: sortedChallenges,
      correctChallengeIds,
      finishedChallengeIds,
      levelFinished: finished
    })

    const challenges = sortedChallenges.map(challenge => {
      const _finished = finishedChallengeIds.has(challenge.id)
      const _correctAnswer = correctChallengeIds.has(challenge.id)

      if (!_finished && !lastUnfinishedChallenge) {
        lastUnfinishedChallenge = challenge
      }

      return {
        ...challenge,
        _finished,
        _correctAnswer,
        _lastUnfinished: lastUnfinishedChallenge === challenge
      }
    })

    return {
      finished,
      blocked,
      blockedStatus,
      rewards,
      challenges,
      finishedChallenges,
      allChallengesFinished,
      lastUnfinishedChallenge,
      hasChallengeToContinue: !allChallengesFinished && finishedChallenges > 0
    }
  }, [progress, level, previousLevel]) as LevelProgress | null

  return {
    level,
    previousLevel,
    levelProgress
  }
}
